import { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../features/AppState";
import {
  upsertFetcherObjects,
  upsertFetcherReport,
} from "../../../../features/fetcherSettingsSlice";
import { setDisplayErrorSnackBar } from "../../../../features/userSlice";
import { FetcherObject } from "../helpers/fetcherSettingsDummyData";
import { nextLocalId } from "../helpers/localIds";
import { nowStamp } from "../helpers/formatters";
import ObjectsPanel from "../ObjectsPanel";
import CreateInterfaceModal from "../Interfaces/CreateInterfaceModal";

export interface ReportDraftSeed {
  name: string;
  iface: string;
  ctype_id: string;
  uri: string;
  objects: { k: string; v: string }[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** pre-fills the form - used by Duplicate, which copies name + objects */
  initial?: ReportDraftSeed | null;
  /** hands the new report's id back to whatever opened this dialog */
  onCreated?: (reportId: string) => void;
}

const STEPS = ["Identity", "Objects"];

const toDraftObjects = (rows: { k: string; v: string }[]): FetcherObject[] =>
  rows.map((row, index) => ({
    id: `draft-${index + 1}`,
    lvl: "report_id",
    raw: "report_id",
    mid: "",
    k: row.k,
    v: row.v,
    ufr: "0",
    mod: "",
  }));

const CreateReportModal = ({ open, onClose, initial, onCreated }: Props) => {
  const dispatch = useDispatch();
  const { interfaces, reports, objects, collectionTypes } = useSelector(
    (state: AppState) => state.fetcherSettings,
  );

  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [interfaceId, setInterfaceId] = useState("");
  const [collectionTypeId, setCollectionTypeId] = useState("");
  const [uri, setUri] = useState("");
  const [draftObjects, setDraftObjects] = useState<FetcherObject[]>([]);
  const [isInterfaceDialogOpen, setIsInterfaceDialogOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setName(initial?.name ?? "");
    setInterfaceId(initial?.iface ?? "");
    setCollectionTypeId(initial?.ctype_id ?? "");
    setUri(initial?.uri ?? "");
    setDraftObjects(toDraftObjects(initial?.objects ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const interfaceOptions = [...interfaces]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((row) => ({ value: row.id, label: `${row.name} (${row.id})` }));

  // a report name is unique per interface + collection type
  const duplicate = reports.find(
    (row) =>
      !!name.trim() &&
      row.iface === interfaceId &&
      row.ctype_id === collectionTypeId &&
      row.name === name.trim(),
  );

  const isIdentityValid =
    !!name.trim() && !!interfaceId && !!collectionTypeId && !duplicate;

  const close = () => {
    onClose();
  };

  const handleCreate = () => {
    // TODO: POST /aladdin_api/v1/data_collection/fetcher_report/ - returns
    // id + shared_uid
    const id = nextLocalId(reports);
    dispatch(
      upsertFetcherReport({
        id,
        name: name.trim(),
        iface: interfaceId,
        ctype_id: collectionTypeId,
        ctype:
          collectionTypes.find((row) => row.id === collectionTypeId)?.name ?? "",
        uri: uri.trim(),
        upath: "",
        configs: 0,
        objects: draftObjects.length,
        dep: 0,
        brands: 0,
        mod: nowStamp(),
      }),
    );
    if (draftObjects.length) {
      let objectId = Number(nextLocalId(objects));
      dispatch(
        upsertFetcherObjects(
          draftObjects.map((row) => ({
            ...row,
            id: String(objectId++),
            lvl: "report_id",
            raw: "report_id",
            mid: id,
            mod: nowStamp(),
          })),
        ),
      );
    }
    dispatch(
      setDisplayErrorSnackBar({
        message: `${name.trim()} (${id}) created.`,
        type: "success",
      }),
    );
    onCreated?.(id);
    onClose();
  };

  const addDraftObject = (fieldType: string, fieldValue: string) =>
    setDraftObjects((previous) => [
      ...previous,
      {
        id: `draft-${Date.now()}`,
        lvl: "report_id",
        raw: "report_id",
        mid: "",
        k: fieldType,
        v: fieldValue,
        ufr: "0",
        mod: "",
      },
    ]);

  return (
    <>
      <Dialog
        open={open}
        onClose={close}
        PaperProps={{ sx: { width: "760px", maxWidth: "94vw" } }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>New Report</DialogTitle>
        <Divider />
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Stepper activeStep={step} sx={{ mb: 3 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {step === 0 ? (
            <Stack spacing={2.5}>
              <TextField
                required
                fullWidth
                size="small"
                label="Report Name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                color="secondary"
              />
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Autocomplete
                  sx={{ flex: 1 }}
                  size="small"
                  options={interfaceOptions}
                  value={
                    interfaceOptions.find(
                      (option) => option.value === interfaceId,
                    ) ?? null
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  onChange={(_, value) => setInterfaceId(value?.value ?? "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label="Interface"
                      color="secondary"
                    />
                  )}
                />
                <Button
                  color="secondary"
                  sx={{ mt: 0.25 }}
                  onClick={() => setIsInterfaceDialogOpen(true)}
                >
                  Create new interface
                </Button>
              </Stack>
              <TextField
                select
                required
                fullWidth
                size="small"
                label="Collection Type"
                value={collectionTypeId}
                onChange={(event) => setCollectionTypeId(event.target.value)}
                color="secondary"
              >
                {collectionTypes.map((type) => (
                  <MenuItem
                    key={type.id}
                    value={type.id}
                    disabled={type.used === 0}
                  >
                    {type.name}
                    {type.used === 0 ? " - not used" : ""}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                size="small"
                label="URI Value"
                placeholder="optional"
                value={uri}
                onChange={(event) => setUri(event.target.value)}
                color="secondary"
              />
              {duplicate && (
                <Alert severity="error">
                  <b>Blocked - 100% duplicate.</b> {duplicate.name} (
                  {duplicate.id}) already exists on this interface with the same
                  collection type.
                </Alert>
              )}
            </Stack>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Objects on this report. object_type and object_value are set
                automatically once it is created — only field_type and
                field_value are needed.
              </Alert>
              <ObjectsPanel
                ownLabel="This report"
                ownObjects={draftObjects}
                markedForDelete={[]}
                onAdd={addDraftObject}
                onEdit={(id, fieldType, fieldValue) =>
                  setDraftObjects((previous) =>
                    previous.map((row) =>
                      row.id === id
                        ? { ...row, k: fieldType, v: fieldValue }
                        : row,
                    ),
                  )
                }
                onMarkDelete={(id) =>
                  setDraftObjects((previous) =>
                    previous.filter((row) => row.id !== id),
                  )
                }
                onUndoDelete={() => undefined}
              />
              {draftObjects.length === 0 && (
                <Typography
                  sx={{ fontSize: "12px", color: "#00000099", mt: 1 }}
                >
                  Without an endpoint or parameters the collector must build
                  this call in code.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {step > 0 && (
            <Button color="secondary" onClick={() => setStep(step - 1)}>
              Back
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button color="secondary" onClick={close}>
            Cancel
          </Button>
          {step === 0 ? (
            <Button
              variant="contained"
              color="secondary"
              disabled={!isIdentityValid}
              onClick={() => setStep(1)}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              disabled={!isIdentityValid}
              onClick={handleCreate}
            >
              Create report
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <CreateInterfaceModal
        open={isInterfaceDialogOpen}
        onClose={() => setIsInterfaceDialogOpen(false)}
        onCreated={(newInterfaceId) => {
          // the new interface comes back pre-selected on the report underneath
          setInterfaceId(newInterfaceId);
          setIsInterfaceDialogOpen(false);
        }}
      />
    </>
  );
};

export default CreateReportModal;
